import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
    ContainerCreateRequest,
    ContainerType,
} from 'src/app/shared/models/container.types';
import { ImgDbWizardSlideCreationService } from '../../img-db/component/services/img-db-wizard-slide-creation.service';
import {
    RobotType,
    WizardSlide,
    WizardSlideContentElement,
    WizardType,
    WIZARD_CARD_SLIDE_ID,
    WIZARD_COLLABORATION_SLIDE_ID,
    WIZARD_FORM_SLIDE_ID,
} from '../models/wizard-slide';

const pibPath = 'assets/img/create-project-modal/pib.jpg';
const antPath = 'assets/img/create-project-modal/ant.jpg';
const nanosaurPath = 'assets/img/create-project-modal/nanosaur.jpeg';
const frankaPandaPath = 'assets/img/create-project-modal/frankaPanda.png';

@Injectable()
export class ContainerWizardSlideCreationService {
    readonly robotSlideContent = [
        {
            image: pibPath,
            cardTitle: 'project.model.wizardSlideData.cardTypePib',
            cardDisc: 'project.model.wizardSlideData.cardTypePibText',
            redirectSlideId: WIZARD_COLLABORATION_SLIDE_ID,
            cardValue: RobotType.PIB,
        },
        {
            image: antPath,
            cardTitle: 'project.model.wizardSlideData.cardTypeAnt',
            cardDisc: 'project.model.wizardSlideData.cardTypeAntText',
            redirectSlideId: WIZARD_COLLABORATION_SLIDE_ID,
            cardValue: RobotType.ANT,
        },
        {
            image: nanosaurPath,
            cardTitle: 'project.model.wizardSlideData.cardTypeNanosaur',
            cardDisc: 'project.model.wizardSlideData.cardTypeNanosaurText',
            redirectSlideId: WIZARD_COLLABORATION_SLIDE_ID,
            cardValue: RobotType.NANOSAUR,
        },
        {
            image: frankaPandaPath,
            cardTitle: 'project.model.wizardSlideData.cardTypeFrankaPanda',
            cardDisc: 'project.model.wizardSlideData.cardTypeFrankaPandaText',
            redirectSlideId: WIZARD_COLLABORATION_SLIDE_ID,
            cardValue: RobotType.FRANKA_PANDA,
        },
    ];

    visualCards$: BehaviorSubject<WizardSlideContentElement[]>;
    imgDbCards$: BehaviorSubject<WizardSlideContentElement[]>;

    constructor(
        private imgDbSlideCreationService: ImgDbWizardSlideCreationService
    ) {}

    createWizardSlides(type: ContainerType): WizardSlide[] {
        let cardSlide = {
            id: WIZARD_CARD_SLIDE_ID,
            root: true,
            type: WizardType.CARD,
            slideTitle: '',
            slideContent: new BehaviorSubject<WizardSlideContentElement[]>([]),
        };

        if (type === ContainerType.PROJECT_MOTORIC) {
            cardSlide.slideTitle = 'project.model.wizardSlideData.chooseARobot';
            cardSlide.slideContent.next(this.robotSlideContent);
        } else if (type === ContainerType.PROJECT_VISUAL) {
            cardSlide.slideTitle =
                'project.model.wizardSlideData.chooseADatabase';

            if (!this.visualCards$) {
                this.visualCards$ = this.imgDbSlideCreationService.createCards(
                    WIZARD_COLLABORATION_SLIDE_ID,
                    'projectWizard'
                );
            }
            cardSlide.slideContent = this.visualCards$;
        } else if (type === ContainerType.IMAGE_DB) {
            cardSlide.slideTitle = 'imgDb.wizard.firstSlideTitle';
            if (!this.imgDbCards$) {
                this.imgDbCards$ = this.imgDbSlideCreationService.createCards(
                    WIZARD_COLLABORATION_SLIDE_ID,
                    'imgDbWizard'
                );
            }
            cardSlide.slideContent = this.imgDbCards$;
        }

        return [
            cardSlide,
            this.createCollaborationSlide(type),
            this.createFormSlide(),
        ];
    }

    changeSlideData(createDto: ContainerCreateRequest): WizardSlide[] {
        let cardSlide = {
            id: WIZARD_CARD_SLIDE_ID,
            root: false,
            type: WizardType.CARD,
            slideTitle: '',
            slideContent: new BehaviorSubject<WizardSlideContentElement[]>([]),
        };

        if (createDto.containerType === ContainerType.PROJECT_VISUAL) {
            cardSlide.slideTitle =
                'project.model.wizardSlideData.chooseADatabase';

            if (!this.visualCards$) {
                this.visualCards$ = this.imgDbSlideCreationService.createCards(
                    WIZARD_COLLABORATION_SLIDE_ID,
                    'projectWizard'
                );
            }
            cardSlide.slideContent = this.visualCards$;
        }

        if (createDto.containerType === ContainerType.PROJECT_MOTORIC) {
            cardSlide.slideTitle = 'project.model.wizardSlideData.chooseARobot';
            cardSlide.slideContent.next(this.robotSlideContent);
        }

        if (createDto.containerType === ContainerType.IMAGE_DB) {
            cardSlide.slideTitle = 'imgDb.wizard.firstSlideTitle';
            if (!this.imgDbCards$) {
                this.imgDbCards$ = this.imgDbSlideCreationService.createCards(
                    WIZARD_COLLABORATION_SLIDE_ID,
                    'imgDbWizard'
                );
            }
            cardSlide.slideContent = this.imgDbCards$;
        }

        return [
            cardSlide,
            this.createCollaborationSlide(createDto.containerType),
            this.createFormSlide(),
        ];
    }

    private createCollaborationSlide(type: ContainerType): WizardSlide {
        return {
            id: WIZARD_COLLABORATION_SLIDE_ID,
            root: false,
            type: WizardType.COLLABORATION,
            slideTitle:
                type === ContainerType.IMAGE_DB
                    ? 'imgDb.wizard.collaborationSlideTitle'
                    : 'project.model.wizardSlideData.collaborationSlideTitle',
            slideContent: new BehaviorSubject<WizardSlideContentElement[]>([]),
        };
    }

    private createFormSlide(): WizardSlide {
        return {
            id: WIZARD_FORM_SLIDE_ID,
            root: false,
            type: WizardType.FORM,
            slideContent: new BehaviorSubject<WizardSlideContentElement[]>([]),
        };
    }
}
